import React, {
  useEffect, useRef, useState,
} from 'react';

import {
  bool, node, number, string,
} from 'prop-types';

// Render a row only while it is near the viewport. Offscreen rows are unmounted and
// replaced by a placeholder of the last measured (or estimated) height so the scroll
// position and scrollbar size stay stable. Consumers must ensure their children are safe
// to unmount and remount — e.g. Antd `Form.Item` preserves values for unmounted items by
// default, which makes it a natural fit.
//
// forceVisible bypasses the IntersectionObserver gate so callers can keep
// <VirtualizedRow> in a stable JSX position while opting children in
// synchronously — e.g. on `beforeprint` to mount every card before the
// blocking print dialog opens.
const VirtualizedRow = ({
  children,
  estimatedHeight = 220,
  rootMargin = '600px 0px',
  forceVisible = false,
}) => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [measuredHeight, setMeasuredHeight] = useState(null);

  useEffect(() => {
    const element = ref.current;
    if (!element) return () => {};

    const intersectionObserver = new IntersectionObserver(
      ([entry]) => setIsVisible(entry.isIntersecting),
      { rootMargin },
    );
    intersectionObserver.observe(element);

    let resizeObserver;
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(([entry]) => {
        const nextHeight = entry.contentRect.height;
        if (nextHeight > 0) setMeasuredHeight(nextHeight);
      });
      resizeObserver.observe(element);
    }

    return () => {
      intersectionObserver.disconnect();
      resizeObserver?.disconnect();
    };
  }, [rootMargin]);

  const shouldRenderChildren = forceVisible || isVisible;
  const reservedHeight = measuredHeight ?? estimatedHeight;
  const placeholderStyle = shouldRenderChildren ? null : { minHeight: reservedHeight };

  return (
    <div ref={ref} style={placeholderStyle}>
      {shouldRenderChildren ? children : null}
    </div>
  );
};

VirtualizedRow.propTypes = {
  children: node.isRequired,
  estimatedHeight: number,
  rootMargin: string,
  forceVisible: bool,
};

export default VirtualizedRow;
